/**
 * @Author: Klaudie <klaudie@foxprofi.cz>
 */

import React, { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import {
  getDocumentTypes,
  getDocumentStatuses,
  getCenters,
  updateInvoice,
} from '@/api/invoices/invoices.api'
import type { AccountingDocument, UpdateInvoiceData } from '@/types/invoices.types'

interface InvoiceEditDialogProps {
  invoice: AccountingDocument | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

export function InvoiceEditDialog({
  invoice,
  open,
  onOpenChange,
  onSuccess,
}: InvoiceEditDialogProps) {
  const [formData, setFormData] = useState<UpdateInvoiceData>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Načtení číselníků
  const { data: documentTypes } = useQuery({
    queryKey: ['documentTypes'],
    queryFn: getDocumentTypes,
  })

  const { data: statuses } = useQuery({
    queryKey: ['documentStatuses'],
    queryFn: getDocumentStatuses,
  })

  const { data: centers } = useQuery({
    queryKey: ['centers'],
    queryFn: getCenters,
  })

  // Inicializace formuláře při otevření dialogu
  useEffect(() => {
    if (invoice) {
      setFormData({
        typeCode: invoice.typeCode,
        statusCode: invoice.statusCode,
        centerId: invoice.centerId,
        number: invoice.number,
        date: invoice.date,
        dateVat: invoice.dateVat,
        dateDue: invoice.dateDue,
        amountTotal: invoice.amountTotal,
        currency: invoice.currency,
        partnerId: invoice.partnerId,
      })
    }
  }, [invoice])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!invoice) return

    setIsSubmitting(true)
    try {
      await updateInvoice(invoice.id, formData)
      toast.success('Faktura byla úspěšně aktualizována')
      onSuccess()
      onOpenChange(false)
    } catch (error) {
      console.error('Error updating invoice:', error)
      toast.error('Chyba při aktualizaci faktury')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (field: keyof UpdateInvoiceData, value: string | number | undefined) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Upravit fakturu</DialogTitle>
          <DialogDescription>
            Upravte údaje faktury. Po uložení se změny projeví v seznamu.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {/* Číslo dokladu */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="number" className="text-right">
                Číslo dokladu
              </Label>
              <Input
                id="number"
                value={formData.number || ''}
                onChange={(e) => handleChange('number', e.target.value)}
                className="col-span-3"
              />
            </div>

            {/* Typ dokladu */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="type" className="text-right">
                Typ dokladu
              </Label>
              <Select
                value={formData.typeCode || ''}
                onValueChange={(value) => handleChange('typeCode', value)}
              >
                <SelectTrigger id="type" className="col-span-3">
                  <SelectValue placeholder="Vyberte typ" />
                </SelectTrigger>
                <SelectContent>
                  {documentTypes?.map((type) => (
                    <SelectItem key={type.code} value={type.code}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="status" className="text-right">
                Status
              </Label>
              <Select
                value={formData.statusCode || ''}
                onValueChange={(value) => handleChange('statusCode', value)}
              >
                <SelectTrigger id="status" className="col-span-3">
                  <SelectValue placeholder="Vyberte status" />
                </SelectTrigger>
                <SelectContent>
                  {statuses?.map((status) => (
                    <SelectItem key={status.code} value={status.code}>
                      {status.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Středisko */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="center" className="text-right">
                Středisko
              </Label>
              <Select
                value={formData.centerId || ''}
                onValueChange={(value) => handleChange('centerId', value)}
              >
                <SelectTrigger id="center" className="col-span-3">
                  <SelectValue placeholder="Vyberte středisko" />
                </SelectTrigger>
                <SelectContent>
                  {centers?.map((center) => (
                    <SelectItem key={center.id} value={center.id}>
                      {center.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Datum přijetí */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="date" className="text-right">
                Datum přijetí
              </Label>
              <Input
                id="date"
                type="date"
                value={formData.date || ''}
                onChange={(e) => handleChange('date', e.target.value)}
                className="col-span-3"
              />
            </div>

            {/* Datum plnění */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="dateVat" className="text-right">
                Datum plnění
              </Label>
              <Input
                id="dateVat"
                type="date"
                value={formData.dateVat || ''}
                onChange={(e) => handleChange('dateVat', e.target.value)}
                className="col-span-3"
              />
            </div>

            {/* Datum splatnosti */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="dateDue" className="text-right">
                Datum splatnosti
              </Label>
              <Input
                id="dateDue"
                type="date"
                value={formData.dateDue || ''}
                onChange={(e) => handleChange('dateDue', e.target.value)}
                className="col-span-3"
              />
            </div>

            {/* Částka */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="amount" className="text-right">
                Částka
              </Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                value={formData.amountTotal || ''}
                onChange={(e) =>
                  handleChange('amountTotal', e.target.value ? parseFloat(e.target.value) : undefined)
                }
                className="col-span-3"
              />
            </div>

            {/* Měna */}
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="currency" className="text-right">
                Měna
              </Label>
              <Input
                id="currency"
                value={formData.currency || ''}
                onChange={(e) => handleChange('currency', e.target.value)}
                className="col-span-3"
                placeholder="CZK"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Zrušit
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Ukládám...' : 'Uložit změny'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
